import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Edit } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

interface Section {
  id: string;
  title: string;
  content_template: string;
  order_number: number;
  is_required: boolean;
  is_locked: boolean;
}

interface SectionsBuilderProps {
  sections: Section[];
  mergeFields: any[];
  onChange: (sections: Section[]) => void;
}

export function SectionsBuilder({ sections, mergeFields, onChange }: SectionsBuilderProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [formData, setFormData] = useState<Partial<Section>>({ title: '', content_template: '' });

  const openDialog = (section?: Section) => {
    if (section) {
      setFormData(section);
      setEditingSection(section);
    } else {
      setFormData({ title: '', content_template: '' });
      setEditingSection(null);
    }
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!formData.title) return;
    if (editingSection) {
      onChange(sections.map((s) => (s.id === editingSection.id ? { ...editingSection, ...formData } : s)));
    } else {
      const newSection: Section = {
        id: crypto.randomUUID(),
        title: formData.title || '',
        content_template: formData.content_template || '',
        order_number: sections.length + 1,
        is_required: false,
        is_locked: false,
      };
      onChange([...sections, newSection]);
    }
    setDialogOpen(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Sections</CardTitle>
        <Button size="sm" onClick={() => openDialog()}>
          <Plus className="h-3 w-3 mr-1" />Add Section
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {sections.map((section) => (
          <div key={section.id} className="flex items-start gap-2 p-3 border rounded-lg">
            <div className="flex-1">
              <h4 className="font-medium">{section.title}</h4>
            </div>
            <Button size="sm" variant="ghost" onClick={() => openDialog(section)}><Edit className="h-3 w-3" /></Button>
          </div>
        ))}
      </CardContent>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingSection ? 'Edit' : 'Add'} Section</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} /></div>
            <div><Label>Content</Label><Textarea value={formData.content_template} onChange={(e) => setFormData({ ...formData, content_template: e.target.value })} rows={8} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
